import * as React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { BrowserRouter as Router } from 'react-router-dom';
import { theme } from './IOPTheme';
import NavUpPanel from './components/layout/NavUpPanel';
import NavLeftPanel from './components/layout/NavLeftPanel';
import AppRoutes from '@/routes';
import websocketService from '@/services/websocket';
import { usePreferencesStore } from './store/preferencesStore';
import { useAuthStore } from './store/authStore';
import { useSystemHealthStore } from './store/systemHealthStore';
import UnauthorizedSnackbar from './components/UnauthorizedSnackbar';
import { NotificationProvider } from './components/NotificationProvider';
import CameraMissingDialog from './components/dialogs/CameraMissingDialog';
import ChamberMissingDialog from './components/dialogs/ChamberMissingDialog';

// Context for unauthorized action
const UnauthorizedSnackbarContext = React.createContext<(msg?: string) => void>(() => {});

export function useUnauthorizedAction() {
  return React.useContext(UnauthorizedSnackbarContext);
}

const App: React.FC = () => {
  const loadPreferencesFromHub = usePreferencesStore((state) => state.loadPreferencesFromHub);
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const sessionExpired = useAuthStore((state) => state.sessionExpired);
  const clearSessionExpired = useAuthStore((state) => state.clearSessionExpired);
  const [prefsLoaded, setPrefsLoaded] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMsg, setSnackbarMsg] = React.useState<string | undefined>(undefined);
  
  // Camera missing dialog state
  const [cameraMissingDialogOpen, setCameraMissingDialogOpen] = React.useState(false);
  const [missingCameraId, setMissingCameraId] = React.useState('');
  const [experimentId, setExperimentId] = React.useState('');

  // Chamber missing dialog state
  const [chamberMissingDialogOpen, setChamberMissingDialogOpen] = React.useState(false);
  const [chamberExperimentId, setChamberExperimentId] = React.useState('');

  const triggerUnauthorized = React.useCallback((msg?: string) => {
    setSnackbarMsg(msg);
    setSnackbarOpen(true);
  }, []);

  React.useEffect(() => {
    async function loadPrefs() {
      try {
        await loadPreferencesFromHub();
      } catch (error) {
        console.error('Failed to load preferences:', error);
      }
      setPrefsLoaded(true);
    }
    loadPrefs();
  }, [loadPreferencesFromHub]);

  React.useEffect(() => {
    // Initialize authentication from sessionStorage
    initializeAuth().catch((error: unknown) => {
      console.error('Failed to initialize authentication:', error);
    });
  }, [initializeAuth]);

  // Surface session-expiry kicks (api.ts logs the user out silently on 401/403)
  React.useEffect(() => {
    if (sessionExpired) {
      setSnackbarMsg('Your session has expired. Please log in again.');
      setSnackbarOpen(true);
      clearSessionExpired();
    }
  }, [sessionExpired, clearSessionExpired]);

  React.useEffect(() => {
    if (!prefsLoaded) return;
    websocketService.connect();
    return () => {
      websocketService.disconnect();
    };
  }, [prefsLoaded]);

  // Handle camera missing alerts
  React.useEffect(() => {
    // Listen for system alerts
    const interval = setInterval(() => {
      const systemHealthStore = useSystemHealthStore.getState();
      const alerts = systemHealthStore.systemAlerts;
      
      // Check for recent camera alerts
      const recentCameraAlerts = alerts.filter(alert =>
        alert.source === 'cameras' &&
        alert.alert_level === 'HIGH' &&
        alert.alert_message.includes('not found') &&
        new Date(alert.timestamp).getTime() > Date.now() - 10000 // Last 10 seconds
      );

      if (recentCameraAlerts.length > 0) {
        const alert: any = recentCameraAlerts[0];
        const match = alert.alert_message.match(/Camera (\w+) not found/);
        if (match) {
          setMissingCameraId(match[1]);
          setExperimentId(alert.exp_id || 'Unknown');
          setCameraMissingDialogOpen(true);
        }
      }

      // Check for recent chamber alerts (Alert 108)
      const recentChamberAlerts = alerts.filter(alert =>
        alert.source === 'chamber' &&
        alert.alert_level === 'HIGH' &&
        alert.alert_message.includes('no chamber connected') &&
        new Date(alert.timestamp).getTime() > Date.now() - 10000 // Last 10 seconds
      );

      if (recentChamberAlerts.length > 0) {
        const alert: any = recentChamberAlerts[0];
        setChamberExperimentId(alert.exp_id || 'Unknown');
        setChamberMissingDialogOpen(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  if (!prefsLoaded) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <span>Loading preferences...</span>
      </div>
    );
  }

  // Remove viewer auto-login logic
  // const { isAuthenticated, login } = useAuthStore();
  // const [authReady, setAuthReady] = React.useState(false);

  // React.useEffect(() => {
  //   if (!isAuthenticated()) {
  //     login('viewer', 'viewer').then(() => setAuthReady(true));
  //   } else {
  //     setAuthReady(true);
  //   }
  //   websocketService.connect();
  //   return () => {
  //     websocketService.disconnect();
  //   };
  // }, [isAuthenticated, login]);

  // Remove authReady check
  // if (!authReady) {
  //   return (
  //     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  return (
    <UnauthorizedSnackbarContext.Provider value={triggerUnauthorized}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NotificationProvider>
        <Router>
          <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <NavLeftPanel />
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <NavUpPanel />
              <Box
                component="main"
                sx={{
                  flexGrow: 1,
                  p: 3,
                  mt: 8,
                  backgroundColor: 'background.default',
                }}
              >
                <AppRoutes />
              </Box>
            </Box>
          </Box>
        </Router>
        <UnauthorizedSnackbar open={snackbarOpen} onClose={() => setSnackbarOpen(false)} message={snackbarMsg} />
        <CameraMissingDialog
          open={cameraMissingDialogOpen}
          onClose={() => setCameraMissingDialogOpen(false)}
          missingCameraId={missingCameraId}
          experimentId={experimentId}
        />
        <ChamberMissingDialog
          open={chamberMissingDialogOpen}
          onClose={() => setChamberMissingDialogOpen(false)}
          experimentId={chamberExperimentId}
        />
        </NotificationProvider>
      </ThemeProvider>
    </UnauthorizedSnackbarContext.Provider>
  );
};

export default App;
